import React from 'react';
import { Drawer, Box, List, Typography, IconButton, Chip, Paper, InputBase, Button } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import { fetchNotifications, markNotificationRead, AppNotification, fetchUnreadCount, markAllNotificationsRead } from '../../services/notificationsService';
import { getManualUnreadIds, setManualUnread, manualUnreadCount, clearManualUnread } from '../../services/notificationsLocalState';
import api from '../../services/api';

interface Props {
  open: boolean;
  onClose: () => void;
  onUnreadChange?: (count: number) => void;
  zIndex?: number;
  onEntered?: () => void;
}

const NotificationDrawer: React.FC<Props> = ({ open, onClose, onUnreadChange, zIndex, onEntered }) => {
  const paperRef = React.useRef<HTMLDivElement | null>(null);
  const [items, setItems] = React.useState<AppNotification[]>([]);
  const [search, setSearch] = React.useState<string>('');
  const [offset, setOffset] = React.useState<number>(0);
  const [hasMore, setHasMore] = React.useState<boolean>(true);
  const [loading, setLoading] = React.useState<boolean>(false);

  const load = async (opts?: { reset?: boolean }) => {
    if (loading) return;
    setLoading(true);
    try {
      const LIMIT = 100;
      const baseOffset = opts?.reset ? 0 : offset;
      const data = await fetchNotifications({ limit: LIMIT, offset: baseOffset });
      const ids = new Set(getManualUnreadIds());
      const mapped = (data || []).map(n => (ids.has(n.id) ? ({ ...n, is_read: false }) : n));
      setHasMore((data || []).length === LIMIT);
      if (opts?.reset) {
        setItems(mapped);
        setOffset(LIMIT);
      } else {
        setItems(prev => {
          const byId = new Map<number, AppNotification>();
          [...prev, ...mapped].forEach(x => byId.set(x.id, x));
          return Array.from(byId.values());
        });
        setOffset(baseOffset + LIMIT);
      }
    } catch {
      // fallback placeholder list
      if (opts?.reset) setItems([]);
      setHasMore(false);
    }
    setLoading(false);
  };

  React.useEffect(() => { if (open) load({ reset: true }); }, [open]);
  // Reset przy zmianie filtra – nie zmieniamy paginacji, tylko filtrujemy lokalnie

  const markAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems(prev => prev.map(x => ({ ...x, is_read: true })));
      clearManualUnread();
      onUnreadChange?.(manualUnreadCount());
    } catch {}
  };

  const onItemClick = async (n: AppNotification) => {
    if (!n.is_read && n.id > 0) {
      await markNotificationRead(n.id);
    }
    setItems(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
    try {
      const c = await fetchUnreadCount();
      if (typeof c === 'number') onUnreadChange?.(c);
    } catch {}
    // Deep link – jeśli entity zawiera trasę
    try {
      const ent = n && (n as any).entity ? JSON.parse((n as any).entity as any) : null;
      if (ent && ent.route) {
        window.location.href = ent.route;
      }
    } catch {}
  };

  const hasUnread = items.some(x => !x.is_read);
  const filteredItems = React.useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return items;
    return items.filter(n => ((n.title || '') + ' ' + (n.message || '')).toLowerCase().includes(q));
  }, [items, search]);
  const unreadCount = React.useMemo(() => items.reduce((acc, x) => acc + (x.is_read ? 0 : 1), 0), [items]);
  const setUnreadCount = React.useCallback((list: AppNotification[]) => {
    const count = list.reduce((acc, x) => acc + (x.is_read ? 0 : 1), 0);
    onUnreadChange?.(count);
  }, [onUnreadChange]);
  const formatYmdHms = (iso: string) => {
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return iso;
    }
  };
  // Preferuj link do PDF nawet jeśli przyjdzie tylko link do dokumentu
  const toPdfLink = (link?: string | null) => {
    const value = String(link || '').trim();
    if (!value) return null;
    try {
      // Google Docs -> pokaż PDF w viewerze (gview) zamiast wymuszać pobranie
      if (value.includes('docs.google.com/document/')) {
        const m = value.match(/\/document\/d\/([a-zA-Z0-9_-]+)/);
        if (m && m[1]) {
          const exportUrl = `https://docs.google.com/document/d/${m[1]}/export?format=pdf`;
          return `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(exportUrl)}`;
        }
      }
      // Ogólny link do pliku na Drive → preferuj podgląd zamiast pobrania
      const byFile = value.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
      const byPlain = value.match(/\/(?:u\/\d+\/)?d\/([a-zA-Z0-9_-]+)/);
      const byQuery = value.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      const id = (byFile && byFile[1]) || (byPlain && byPlain[1]) || (byQuery && byQuery[1]) || null;
      if (id) return `https://drive.google.com/file/d/${id}/preview`;
    } catch {}
    return null;
  };
  const toDrivePreview = (link?: string | null) => {
    const value = String(link || '').trim();
    if (!value) return null;
    try {
      const byFile = value.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
      const byPlain = value.match(/\/(?:u\/\d+\/)?d\/([a-zA-Z0-9_-]+)/);
      const byQuery = value.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      const id = (byFile && byFile[1]) || (byPlain && byPlain[1]) || (byQuery && byQuery[1]) || null;
      if (id) return `https://drive.google.com/file/d/${id}/preview`;
    } catch {}
    return null;
  };
  const openProtocolPdfPreview = async (entity: any) => {
    if (!entity || !entity.project_id || !entity.id) return false;
    try {
      const projectId = entity.project_id;
      const res = await api.get(`/protocols/project/${encodeURIComponent(projectId)}`);
      const items = res?.data?.data || [];
      const match = items.find((x: any) => String(x.id) === String(entity.id));
      const pdfUrl = match?.generated_pdf_url || null;
      const previewUrl = toDrivePreview(pdfUrl) || toPdfLink(pdfUrl);
      if (previewUrl) {
        window.open(previewUrl, '_blank', 'noopener');
        return true;
      }
    } catch {}
    return false;
  };
  const openSubcontractorFolder = async (entity: any) => {
    if (!entity || !entity.id) return false;
    const id = entity.id;
    // 1) Próba bezpośredniego detalu (JWT)
    try {
      const res = await api.get(`/subcontractors/${encodeURIComponent(id)}`);
      const folderId = res?.data?.drive_folder_id || null;
      if (folderId) {
        const url = `https://drive.google.com/drive/folders/${folderId}`;
        window.open(url, '_blank', 'noopener');
        return true;
      }
    } catch {}
    // 2) Fallback: lista dla projektu (endpoint publiczny)
    try {
      const projectId = entity.project_id;
      if (projectId) {
        const res2 = await api.get(`/subcontractors/project/${encodeURIComponent(projectId)}`);
        const list = Array.isArray(res2?.data) ? res2.data : [];
        const found = list.find((s: any) => String(s.id) === String(id));
        const folderId2 = found?.drive_folder_id || null;
        if (folderId2) {
          const url = `https://drive.google.com/drive/folders/${folderId2}`;
          window.open(url, '_blank', 'noopener');
          return true;
        }
      }
    } catch {}
    return false;
  };
  const toggleRead = async (e: React.MouseEvent, n: AppNotification) => {
    e.stopPropagation();
    if (!n.is_read) {
      try { if (n.id > 0) await markNotificationRead(n.id); } catch {}
      setManualUnread(n.id, false);
      setItems(prev => {
        const next = prev.map(x => x.id === n.id ? { ...x, is_read: true } : x);
        setUnreadCount(next);
        return next;
      });
      return;
    }
    // brak endpointu „unread” – przełącz lokalnie
    setManualUnread(n.id, true);
    setItems(prev => {
      const next = prev.map(x => x.id === n.id ? { ...x, is_read: false } : x);
      setUnreadCount(next);
      return next;
    });
  };

  // Infinite scroll – nasłuch na Paperze szuflady
  React.useEffect(() => {
    const el = paperRef.current;
    if (!el) return;
    const onScroll = () => {
      const threshold = 240; // px przed dołem
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
      if (atBottom && hasMore && !loading) {
        load();
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasMore, loading, offset]);

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={open}
      onClose={onClose}
      SlideProps={{ onEntered: () => { try { onEntered?.(); } catch {} } } as any}
      sx={{ zIndex: zIndex ?? ((theme) => theme.zIndex.drawer) }}
      ModalProps={{ disableEnforceFocus: true, disableAutoFocus: true, keepMounted: true, BackdropProps: { sx: { backgroundColor: 'rgba(0,0,0,0.35)' } } }}
      PaperProps={{ 
        ref: paperRef as any,
        sx: { width: 420, maxWidth: '100vw', minWidth: 320, top: 0, height: '100vh', borderRadius: 0, right: 0, pointerEvents: 'auto', zIndex: zIndex ?? (theme => theme.zIndex.drawer), overflowY: 'auto' },
        onTransitionEnd: (e: React.TransitionEvent<HTMLDivElement>) => { try { if ((e as any).propertyName === 'transform') onEntered?.(); } catch {} }
      }}
    >
      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fff', borderBottom: '1px solid #eee' }}>
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="img" src="/assets/topbar/bell.svg" alt="notifications" sx={{ width: 20, height: 20 }} />
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>Rejestr zdarzeń</Typography>
          </Box>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Button
            variant="contained"
            onClick={markAll}
            disabled={!hasUnread}
            sx={{
              width: 116,
              height: 40,
              borderRadius: '4px',
              textTransform: 'none',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              wordBreak: 'keep-all',
              backgroundColor: '#4040DB',
              color: '#fff',
              '&:hover': { backgroundColor: '#1F1FB0' },
              '&:active': { backgroundColor: '#1A1A98' }
            }}
          >
            {`Odznacz (${unreadCount})`}
          </Button>
          <Paper
            elevation={0}
            sx={{
              px: 0,
              py: 0,
              ml: 1,
              display: 'flex',
              alignItems: 'center',
              borderRadius: '4px',
              backgroundColor: '#f9f9f9',
              border: '1px solid #ededed',
              height: 36,
              width: 260,
              '&:hover': { borderColor: '#B9B9B9' }
            }}
          >
            <Box component="img" src="/assets/topbar/search.svg" alt="search" sx={{ ml: 1.5 }} />
            <InputBase
              placeholder="Szukaj zdarzenia"
              value={search}
              onChange={(e)=>setSearch(e.target.value)}
              onKeyDown={(e)=>{ if(e.key==='Escape'){ e.preventDefault(); e.stopPropagation(); try { (e.currentTarget as HTMLInputElement).blur(); } catch {} } }}
              sx={{ pl: 1.5, pr: 1.5, flex: 1, fontSize: 14 }}
              inputProps={{ 'aria-label': 'szukaj zdarzenia', 'data-role': 'notifications-filter' }}
            />
          </Paper>
        </Box>
      </Box>
      <List>
        {filteredItems.map(n => {
          let route: string | undefined;
          let externalLink: string | undefined;
          let entityObj: any = null;
          let extraChips: Array<JSX.Element> = [];
          try {
            const ent = n && (n as any).entity ? JSON.parse((n as any).entity as any) : null;
            entityObj = ent;
            // Candidate KPIs chips (IQ/ST) – pokazuj tylko jeśli istnieją wartości
            if (String(n.type || '') === 'candidate.created' && ent) {
              const toNum = (v: any) => {
                const num = Number(v);
                return Number.isFinite(num) ? num : null;
              };
              const iq = toNum(ent.iq_percent);
              const st = toNum(ent.stress_percent);
              const iqTaken = typeof ent.iq_taken === 'boolean'
                ? ent.iq_taken
                : (iq != null && iq > 0); // bez flag: 0% traktuj jako „brak”
              const stTaken = typeof ent.stress_taken === 'boolean'
                ? ent.stress_taken
                : (st != null && st > 0); // bez flag: 0% traktuj jako „brak”
              // Pastel chips; „brak” z szarym tłem
              if (iqTaken || iq != null) {
                extraChips.push(
                  <Chip
                    key="kpi-iq"
                    label={`IQ: ${iqTaken ? `${iq ?? 0}%` : 'brak'}`}
                    size="small"
                    sx={{
                      height: 18, borderRadius: '4px', fontSize: 10,
                      bgcolor: iqTaken ? '#E6F7E9' : '#F4F4F5',
                      color: iqTaken ? '#1F7A39' : '#52525B',
                      '& .MuiChip-label': { px: 0.8 }
                    }}
                  />
                );
              }
              if (stTaken || st != null) {
                extraChips.push(
                  <Chip
                    key="kpi-st"
                    label={`ST: ${stTaken ? `${st ?? 0}%` : 'brak'}`}
                    size="small"
                    sx={{
                      height: 18, borderRadius: '4px', fontSize: 10,
                      bgcolor: stTaken ? '#FFE6EE' : '#F4F4F5',
                      color: stTaken ? '#C81E5D' : '#52525B',
                      '& .MuiChip-label': { px: 0.8 }
                    }}
                  />
                );
              }
            }
            // Link do dokumentu (np. POKR/POCR) – preferuj bezpośredni link jeśli dostępny
            if (ent) {
              // Jeśli backend podał bezpośrednią trasę:
              // - dla pakietów otwieraj w nowej karcie (externalLink)
              // - w pozostałych przypadkach użyj nawigacji w tej karcie (route)
              if (ent.route && typeof ent.route === 'string') {
                if (String(n.type || '').includes('package')) {
                  externalLink = String(ent.route);
                } else {
                  route = String(ent.route);
                }
              }
              // Preferuj PDF
              if (ent.pdf_link) {
                // Jeśli backend podał link wymuszający pobieranie, spróbuj zamienić na podgląd
                externalLink = toPdfLink(String(ent.pdf_link)) || String(ent.pdf_link);
              } else if (n.type === 'transfer_invoice' && ent.id) {
                // Powiadomienie o fakturze – otwórz stronę faktur z podglądem
                externalLink = `/accounting/purchases?invoice=${encodeURIComponent(ent.id)}`;
              } else if ((String(n.type || '').includes('inquiry')) && ent.project_id && ent.id) {
                // Zapytanie – nowa karta: projekt → Kontraktowanie → Zapytania + otwórz dialog wiadomości
                externalLink = `/projects/${encodeURIComponent(ent.project_id)}?open=inquiries&inquiry=${encodeURIComponent(ent.id)}`;
              } else if ((n.type === 'package.created' || n.type === 'package') && ent.project_id) {
                // Pakiet – zawsze nowa karta: preferuj route z backendu, w przeciwnym razie zbuduj lokalnie
                externalLink = ent.route ? String(ent.route) : `/projects/${encodeURIComponent(ent.project_id)}?open=packages`;
                route = undefined;
              } else if (String(n.type || '').includes('requester.leave') && ent.route) {
                // Wniosek urlopowy – użyj route z backendu
                route = String(ent.route);
              } else if (ent.link || ent.doc_link) {
                // Jeśli to link do Google Docs – pomiń gview (często brak podglądu dla prywatnych)
                const raw = String(ent.link || ent.doc_link);
                if (raw.includes('docs.google.com/document/')) {
                  externalLink = undefined; // zostaw fallback do pobrania URL PDF z API
                } else {
                  // Dla plików Drive spróbuj zbudować /preview
                  const forcedPdf = toPdfLink(raw);
                  externalLink = forcedPdf || raw;
                }
              }
            }
            // Fallback: trasa do projektu/podwykonawcy
            if (!externalLink && ent && ent.project_id && ent.subcontractor_id) {
              route = `/projects/${encodeURIComponent(ent.project_id)}?subId=${encodeURIComponent(ent.subcontractor_id)}`;
            }
          } catch {}
          return (
            <Box
              key={n.id}
              onClick={() => onItemClick(n)}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.2,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                backgroundColor: !n.is_read ? '#F5F5FF' : 'transparent',
                '&:hover': { backgroundColor: '#F5F5FF' },
                cursor: 'pointer'
              }}
            >
              <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: '2px', cursor: 'pointer' }} onClick={(e)=>toggleRead(e, n)} title={n.is_read ? 'Oznacz jako nieprzeczytane' : 'Oznacz jako przeczytane'}>
                <NotificationsIcon sx={{ color: n.is_read ? 'action.disabled' : 'error.main' }} fontSize="small" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32 !important' }}>
                  {n.title}
                </Typography>
                {!!n.message && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.2, wordBreak: 'break-word' }}>
                    {n.message}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, justifyContent: 'space-between', width: '100%' }}>
                  <Chip
                    label={formatYmdHms(n.created_at)}
                    size="small"
                    sx={{
                      bgcolor: '#FFF5CC',
                      color: '#020242',
                      borderRadius: '4px',
                      minHeight: 21,
                      height: 21,
                      '& .MuiChip-label': {
                        px: 1,
                        py: 0,
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '11px',
                        fontWeight: 400,
                        lineHeight: '13px',
                        color: '#020242'
                      }
                    }}
                  />
                  {extraChips.map((c, idx) => (
                    <React.Fragment key={idx}>{c}</React.Fragment>
                  ))}
                  {(route || externalLink || (entityObj && (entityObj.entityType === 'ProtocolDocument' || entityObj.entityType === 'Subcontractor'))) && (
                    <Chip
                      label="Przejdź"
                      size="small"
                      clickable
                      color="primary"
                      variant="outlined"
                      onClick={async (e) => {
                        e.stopPropagation();
                        // Jeśli to protokół – zawsze spróbuj najpierw otworzyć PDF z bazy
                        if (entityObj && entityObj.entityType === 'ProtocolDocument' && entityObj.id) {
                          const ok = await openProtocolPdfPreview(entityObj);
                          if (ok) return;
                        }
                        // Jeśli to podwykonawca – otwórz folder na Drive
                        if (entityObj && entityObj.entityType === 'Subcontractor' && entityObj.id) {
                          const ok = await openSubcontractorFolder(entityObj);
                          if (ok) return;
                        }
                        if (externalLink) {
                          try { window.open(externalLink as string, '_blank', 'noopener'); } catch {}
                          return;
                        }
                        if (route) {
                          window.location.href = route as string;
                          return;
                        }
                      }}
                      sx={{
                        height: 18,
                        borderRadius: '4px',
                        fontWeight: 700,
                        fontSize: 10,
                        ml: 'auto',
                        '& .MuiChip-label': { px: 0.8 },
                        '&:hover': { backgroundColor: 'primary.light', color: '#fff', borderColor: 'primary.light' }
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
        {items.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">Brak powiadomień.</Typography>
          </Box>
        )}
      </List>
    </Drawer>
  );
};

export default NotificationDrawer;


